import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastNotificationService } from 'src/app/shared/toast-notification/toast-notification.service';
import { ToastV2Model, ToastV2TypeEnum } from '../../models/toast-v2.model';
import { NgFor } from '@angular/common';
import { ToastItemComponent } from './toast-item/toast-item.component';

@Component({
	standalone: true,
	selector: 'app-toast-v2',
	templateUrl: './toast-v2.component.html',
	styleUrls: ['./toast-v2.component.scss'],
	animations: [
		trigger('toastAnimation', [
			transition(':enter', [
				style({
					transform: 'translateX(100%)',
					opacity: 0
				}),
				animate(
					'300ms cubic-bezier(.5, 1.6, .4, 1)',
					style({
						transform: 'translateX(0)',
						opacity: 1
					})
				)
			]),
			transition(':leave', [
				animate(
					'150ms ease-in-out',
					style({
						opacity: 0
					})
				),
				animate(
					'150ms ease-in-out',
					style({
						height: 0,
						marginBottom: 0,
						paddingTop: 0,
						paddingBottom: 0,
						overflow: 'hidden'
					})
				)
			])
		])
	],
	imports: [NgFor, ToastItemComponent]
})
export class ToastV2Component implements OnInit {
	toasts: { id: number; data: ToastV2Model }[] = [];
	timeoutIds: Record<number, number> = {};

	unSubscriber: Subject<void> = new Subject<void>();

	ToastV2TypeEnum = ToastV2TypeEnum;

	constructor(private toastNotificationService: ToastNotificationService) {}

	ngOnInit(): void {
		this.initToastNotificationServiceListeners();
	}

	initToastNotificationServiceListeners(): void {
		this.toastNotificationService
			.getToasts()
			.pipe(takeUntil(this.unSubscriber))
			.subscribe(toast => {
				this.addToast(toast);
			});
	}

	addToast(toast: ToastV2Model) {
		const toastId = Math.random();

		this.toasts.push({
			id: toastId,
			data: toast
		});

		// `duration: 0` is deliberate: the toast persists until the person
		// dismisses it (the close button) or its own action runs — see
		// `toast-v2.model.ts`. No timeout is armed, so `removeToast` is the only
		// thing that can ever clear it.
		if (toast.duration === 0) {
			return;
		}

		this.timeoutIds[toastId] = setTimeout(() => {
			this.removeToast(toastId);
		}, toast.duration) as unknown as number;
	}

	/**
	 * The action button was pressed. What the action means belongs to whoever
	 * raised the toast, so it is run as handed over and the toast is dismissed —
	 * acting on it is the end of its life, exactly like closing it.
	 */
	runToastAction(toast: { id: number; data: ToastV2Model }) {
		toast.data.action?.run();
		this.removeToast(toast.id);
	}

	removeToast(toastId: number) {
		clearTimeout(this.timeoutIds[toastId]);

		delete this.timeoutIds[toastId];

		this.toasts = this.toasts.filter(toast => toast.id !== toastId);
	}

	removeAllToasts() {
		this.toasts = [];

		Object.values(this.timeoutIds).forEach(timeoutId => {
			clearTimeout(timeoutId);
		});

		this.timeoutIds = {};
	}

	trackToast(index: number, toast: { id: number; data: ToastV2Model }) {
		return toast.id;
	}

	ngOnDestroy(): void {
		this.removeAllToasts();

		this.unSubscriber.next();
		this.unSubscriber.complete();
	}
}
